import { useState, useEffect, useCallback, useRef } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage
} from '@/components/ui/form'
import { FloatingInput } from '@/components/ui/floating-input'
import { FloatingSelect } from '@/components/ui/floating-select'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/hooks/use-toast'
import { Loader2, AlertCircle } from 'lucide-react'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useUser } from '@/hooks/use-user'

import { formatFullNameToInitials } from '@/utils/format'
import WaiverSignatureCanvas from './WaiverSignatureCanvas'

const waiverFormSchema = z
    .object({
        fullName: z.string().min(2, 'Full name must be at least 2 characters'),
        emailAddress: z.string().email('Please enter a valid email address'),
        signature: z.string().min(2, 'Signature is required'),
        agreement: z.boolean().refine((val) => val === true, {
            message: 'You must agree to the terms'
        }),
        attendanceStatus: z.string().default('single_male'),
        partnerFullName: z.string().optional(),
        partnerInitials: z.string().optional(),
        partnerSignature: z.string().optional(),
        initials: z.string().min(2, 'Initials must be at least 2 characters'),
        phoneNumber: z.string().min(10, 'Phone number must be at least 10 digits').regex(/^\+?\d+$/, 'Phone number must be numeric')
    })
    .refine(
        (data) => {
            if (data.attendanceStatus === 'couple') {
                return data.partnerFullName && data.partnerFullName.length > 0
            }
            return true
        },
        {
            message: 'Partner full name is required for couple attendance status',
            path: ['partnerFullName']
        }
    )
    .refine(
        (data) => {
            if (data.attendanceStatus === 'couple') {
                return data.partnerInitials && data.partnerInitials.length > 0
            }
            return true
        },
        {
            message: 'Partner initials are required for couple attendance status',
            path: ['partnerInitials']
        }
    )
    .refine(
        (data) => {
            if (data.attendanceStatus === 'couple') {
                return !!data.partnerSignature && data.partnerSignature.length > 0
            }
            return true
        },
        {
            message: 'Partner signature is required for couple attendance status',
            path: ['partnerSignature']
        }
    )

export interface WaiverFormResult {
    fullName: string
    emailAddress: string
    signature: string
    attendanceStatus: string
    partnerFullName?: string
    initials: string
    partnerInitials?: string
    partnerSignature?: string
    phoneNumber: string
    type: string
}

interface WaiverFormProps {
    type: 'secrets_hideaway' | 'club_swinkster'
    onComplete: (data: WaiverFormResult) => void
    onError: (error: string) => void
    isTransitioning?: boolean
    savedFormResult: WaiverFormResult | null
}



export default function WaiverForm({
    type,
    onComplete,
    onError,
    savedFormResult,
    isTransitioning
}: WaiverFormProps) {
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [submitError, setSubmitError] = useState<string | null>(null)
    const { toast } = useToast()
    const { walletAddress, email: userEmail } = useUser()

    const form = useForm<z.infer<typeof waiverFormSchema>>({
        resolver: zodResolver(waiverFormSchema),
        defaultValues: {
            fullName: '',
            emailAddress: '',
            signature: '',
            phoneNumber: '',
            attendanceStatus: 'single_male',
            partnerFullName: '',
            initials: '',
            partnerInitials: '',
            partnerSignature: '',
            agreement: false
        },
        mode: 'onChange'
    })

    useEffect(() => {
        form.reset()

        return () => {
            form.reset()
            setSubmitError(null)
            setIsSubmitting(false)
        }
    }, [type, form])

    useEffect(() => {
        form.setValue('emailAddress', userEmail ?? '')

        if (savedFormResult) {
            form.setValue('fullName', savedFormResult.fullName)
            form.setValue('emailAddress', savedFormResult.emailAddress)
            form.setValue('phoneNumber', savedFormResult.phoneNumber)
            form.setValue('attendanceStatus', savedFormResult.attendanceStatus)
            form.setValue('partnerFullName', savedFormResult.partnerFullName)
            form.setValue('partnerInitials', savedFormResult.partnerInitials)
            form.setValue('initials', savedFormResult.initials)
        }
    }, [savedFormResult, type]);

    const updateInitials = () => {
        const values = form.getValues()

        if (values.fullName && !values.initials) {
            form.setValue('initials', formatFullNameToInitials(values.fullName))
            form.clearErrors('initials')
        }
    }

    const updatePartnerInitials = () => {
        const values = form.getValues()

        if (values.partnerFullName && !values.partnerInitials) {
            form.setValue('partnerInitials', formatFullNameToInitials(values.partnerFullName))
            form.clearErrors('partnerInitials')
        }
    }

    const attendanceStatusValue = form.watch('attendanceStatus')

    const onSubmit = useCallback(
        async (data: z.infer<typeof waiverFormSchema>) => {
            if (isTransitioning) return

            if (!data.signature) {
                setSubmitError('Please provide a signature')
                toast({
                    title: 'Error',
                    description: 'Please provide a signature',
                    variant: 'destructive'
                })
                return
            }

            if (data.attendanceStatus === 'couple' && !data.partnerSignature) {
                setSubmitError('Please provide a signature from your partner')
                toast({
                    title: 'Error',
                    description: 'Please provide a signature from your partner',
                    variant: 'destructive'
                })
                return
            }

            setSubmitError(null)
            setIsSubmitting(true)

            try {
                // const response = await apiFetch('/v1/waivers', {
                //     method: 'POST',
                //     headers: { 'Content-Type': 'application/json' },
                //     body: JSON.stringify({
                //         type,
                //         fullName: data.fullName.trim(),
                //         emailAddress: data.emailAddress.trim(),
                //         signature: data.signature.trim(),
                //         customerAddress: walletAddress,
                //         timestamp: new Date().toISOString()
                //     })
                // })

                // if (!response.ok) {
                //     const errorData = await response.json()
                //     throw new Error(errorData.message || 'Failed to submit waiver')
                // }

                // toast({
                //     title: 'Waiver Submitted Successfully',
                //     description: `${type === 'secrets_hideaway' ? 'Secrets Hideaway' : 'Club Swinkster'
                //         } waiver has been signed and submitted.`
                // })

                onComplete({
                    attendanceStatus: data.attendanceStatus,
                    fullName: data.fullName.trim(),
                    emailAddress: data.emailAddress.trim(),
                    signature: data.signature.trim(),
                    phoneNumber: data.phoneNumber.trim(),
                    partnerFullName: data.partnerFullName?.trim(),
                    initials: data.initials.trim(),
                    partnerInitials: data.partnerInitials?.trim(),
                    partnerSignature: data.partnerSignature?.trim(),
                    type
                })
            } catch (error) {
                const errorMessage =
                    error instanceof Error ? error.message : 'Failed to submit waiver'
                setSubmitError(errorMessage)
                onError(errorMessage)
                toast({
                    title: 'Error',
                    description: errorMessage,
                    variant: 'destructive'
                })
            } finally {
                setIsSubmitting(false)
            }
        },
        [type, isTransitioning, onComplete, onError, toast, walletAddress]
    )



    return (
        <Card
            className={`p-6 transition-all duration-300 bg-[#3C3C3C] border-0 ${isTransitioning ? 'opacity-50 pointer-events-none' : 'opacity-100'
                }`}>
            <h2 className="text-2xl font-bold mb-4 text-[#ffffff]">
                {type === 'secrets_hideaway' ? 'Secrets Hideaway' : 'Club Swinkster'}{' '}
                Waiver
            </h2>
            <p className="text-sm text-[#BABABB] mb-6">
                Please read the waiver carefully before signing.
            </p>

            <ScrollArea className="h-[450px] mb-6 rounded-md border border-[#4C4C4C] p-6 bg-[#2F2F32]">
                <div
                    className="prose prose-base dark:prose-invert max-w-none space-y-8
          [&>section]:mb-10 [&>section:last-child]:mb-0
          prose-headings:text-[#ffffff] prose-headings:font-bold prose-headings:text-2xl
          prose-p:text-[#C7C7C7] prose-p:leading-relaxed prose-p:mb-4
          prose-li:text-[#C7C7C7] prose-li:leading-relaxed
          [&>section>h3]:text-2xl [&>section>h3]:font-bold [&>section>h3]:text-[#ffffff] [&>section>h3]:mb-6
          [&>section>ul]:mt-4 [&>section>ul]:space-y-3 [&>section>ul]:pl-6
          [&_p]:text-[#C7C7C7] [&_b]:text-[#ffffff] [&_h3]:text-[#ffffff]">
                    {getWaiverContent(type)}
                </div>
            </ScrollArea>

            {submitError && (
                <Alert variant="destructive" className="mb-4">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{submitError}</AlertDescription>
                </Alert>
            )}

            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <FormField
                        control={form.control}
                        name="fullName"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <FloatingInput
                                        {...field}
                                        label="Full Legal Name"
                                        onBlur={updateInitials}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="initials"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <FloatingInput
                                        {...field}
                                        label="Your Initials"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="emailAddress"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <FloatingInput
                                        type="email"
                                        {...field}
                                        label="Email Address"
                                        autoComplete="email"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="phoneNumber"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <FloatingInput
                                        {...field}
                                        autoComplete="tel"
                                        inputMode="numeric"
                                        label="Phone Number"
                                        onChange={(e) => {
                                            // Strip all non-numeric characters except leading +
                                            const value = e.target.value
                                            const cleaned = value.startsWith('+')
                                                ? '+' + value.slice(1).replace(/\D/g, '')
                                                : value.replace(/\D/g, '')
                                            field.onChange(cleaned)
                                        }}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="attendanceStatus"
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <FloatingSelect
                                        label="Attendance Status"
                                        value={field.value}
                                        onValueChange={field.onChange}
                                        options={[
                                            { value: 'single_male', label: 'Single male' },
                                            { value: 'single_female', label: 'Single female' },
                                            { value: 'couple', label: 'Couple' }
                                        ]}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {attendanceStatusValue === 'couple' && (
                        <FormField
                            control={form.control}
                            name="partnerFullName"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <FloatingInput
                                            {...field}
                                            label="Partner Full Name"
                                            onBlur={updatePartnerInitials}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    {attendanceStatusValue === 'couple' && (
                        <FormField
                            control={form.control}
                            name="partnerInitials"
                            render={({ field }) => (
                                <FormItem>
                                    <FormControl>
                                        <FloatingInput
                                            {...field}
                                            label="Partner Initials"
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    {/* <FormControl>
                                    <Input
                                        {...field}
                                        placeholder="Type your full name exactly as entered above"
                                        className="bg-background/50"
                                    />
                                </FormControl>
                                <FormDescription className="text-sm text-foreground/70">
                                    Your signature must match your full name exactly (not case
                                    sensitive)
                                </FormDescription> */}


                    <FormField
                        control={form.control}
                        name="signature"
                        render={({ field }) => (
                            <WaiverSignatureCanvas value={field.value} onChange={field.onChange} />
                        )}
                    />

                    {attendanceStatusValue === 'couple' && <FormField
                        control={form.control}
                        name="partnerSignature"
                        render={({ field }) => (
                            <WaiverSignatureCanvas title="Partner's Signature" value={field.value} onChange={field.onChange} />
                        )}
                    />}

                    <FormField
                        control={form.control}
                        name="agreement"
                        render={({ field }) => (
                            <FormItem className="rounded-lg border-2 border-amber-500/50 bg-amber-500/10 p-4">
                                <div className="flex flex-row items-start space-x-3 space-y-0">
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                            className="mt-0.5 h-5 w-5 border-2 border-amber-500 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                                        />
                                    </FormControl>
                                    <div className="space-y-1 leading-none">
                                        <FormLabel className="text-[#ffffff] font-medium cursor-pointer">
                                            I have read, understood, and agree to the terms and
                                            conditions outlined in this waiver
                                        </FormLabel>
                                    </div>
                                </div>
                                <FormMessage className="mt-2 ml-8" />
                            </FormItem>
                        )}
                    />

                    <Button
                        type="submit"
                        className="w-full"
                        disabled={isSubmitting || isTransitioning}>
                        {isSubmitting ? (
                            <>
                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                Submitting...
                            </>
                        ) : (
                            'Sign and Continue'
                        )}
                    </Button>
                </form>
            </Form>
        </Card>
    )
}

function getWaiverContent(type: 'secrets_hideaway' | 'club_swinkster') {
    if (type === 'secrets_hideaway') {
        return <>
            <p>
                To protect our attendees, guests, members and everyone at Secrets, everyone is required to
                read and agree to follow and abide by all the terms described below. And to confirm their
                agreement by initialing each term and signing at the bottom. Secrets will match your name with
                your photo identification. Secrets Hideaway Resort & Spa, Secret Enterprises LLC, Secrets
                Development Group LLC, SecretsFL.com, Club Secret and any and all its owners, partners,
                investors, condo owners, managers, employees, sub-contractors, hosts, staff, directors,
                officers, volunteers, representatives, agents, activity or event holders, sponsors,
                volunteers, legal representatives or assignees, and anyone acting with authority and
                permission shall hereinafter in this document be recognized and known as “Secrets”
            </p>

            <p>
                <b>1) Secrets Hideaway is a private membership club.</b> All guests must be a member, and have
                an active membership card, show valid photo ID, and be of legal drinking age (21 years of age
                or older) for entry. Secrets Hideaway and Club Secret reserves the right to suspend/revoke any
                guest membership at any time without refund.
            </p>

            <p>
                <b>2) No cameras, camera phones, video equipment or recorders</b> in the Play Area or where
                adult activity/nudity takes place (including the pool area)! Cell phones and cameras are
                permitted in nightclub/bar areas and we agree to only take photos of personal friends known to
                us with their explicit permission only, and with no other people in the background. Secrets
                reserves the right to inspect all containers, bags, and backpacks for video or camera
                equipment. Secrets also reserves the right to inspect/confiscate any cell phone or other
                electronic device to ensure guest privacy and security.
            </p>

            <p>
                <b>3) No sexual activity or nudity in:</b> Lobby,
                hallways, Lobby bathrooms, restaurant, Nightclub, parking lot. Please make sure you act and
                dress appropriately when in these areas. Violators risk ejection from the party without
                refund.
            </p>

            <p>
                <b>4) Hotel policies:</b> Violators of any hotel
                rules, guidelines and policies, will be asked to leave without a refund.
            </p>

            <p>
                <b>5) Discretion:</b> What happens here stays
                here. We are a private and discrete social club. People attending expect that their names,
                professions and actions will not be mentioned or broadcast on the airways, internet, or even
                by word of mouth. “What you see here, what you hear, here, remains here when you leave here”.
            </p>
            <p>
                <b>6) Lost/Misplaced/Stolen Items:</b> Secrets
                are not liable for any lost, misplaced, or stolen items.
            </p>
            <p>
                <b>7) Injury:</b> I/we do hereby release Secrets
                from all liability arising from anything resulting in injury of any kind.
            </p>
            <p>
                <b>8) Drugs:</b> Just Say No! Illegal drugs are
                against the law and are NOT tolerated. You will be asked to leave without refund, banned from
                any further events, and possibly arrested and prosecuted.
                <b>This is a zero tolerance policy!</b>
            </p>
            <p>
                <b>9) Alcohol:</b> If you become overly
                intoxicated and disruptive, you may be asked to leave without a refund. No outside alcohol is
                allowed to be brought into the club, play/adult areas, and gated pool areas.
                <b>This is a zero tolerance policy!</b>
            </p>
            <p>
                <b>10) Weapons:</b> Weapons of any kind are
                strictly prohibited anywhere on Secrets’ property. If in possession of a weapon, you will be
                immediately ejected from the property and banned from future events.
                <b>This is a zero tolerance policy!</b>
            </p>
            <p>
                <b>11) Wristbands:</b> Wristbands must be worn at
                all times. You will not be allowed on property without a wristband purchased at the lobby
                desk. If you remove your wristband you must either leave the property or purchase a new
                wristband. If your wristband breaks, we will exchange your old wristband for a new one. A
                broken wristband must be traded for a new one.
            </p>
            <p>
                <b>12) Unsolicited Sponsors:</b> We have sponsors
                that have paid to be here. Do Not solicit or you will be removed from the property and any
                materials will be destroyed.
                <b>This is a zero tolerance policy!</b>
            </p>
            <p>
                <b>13) Parties/Events:</b> Please be aware that
                these are alternate lifestyle related events (swingers, bi-sexual women and men) so if this
                type of party offends you please do NOT attend.
            </p>
            <p>
                <b>14) Prostitution is Against The Law:</b> Soliciting or exchanging money for sexual acts is
                a crime. Even joking about it will get you ejected and banned from Secrets property and
                possibly arrested and prosecuted. <b>This is a zero tolerance policy!</b>
            </p>
            <p>
                <b>By signing this form,</b> I/we do hereby release Secrets and its members and condo owners
                from any and all liability arising from a result of our attendance at Secrets property, or as
                a result of subsequent meetings or circumstances with any attendee or guest, now or in the
                future. I/We furthermore agree to abide by any and all rules as set forth by Secrets. We agree
                to keep all information confidential and that we are not acting as a part of or on behalf of
                any media or governmental enforcement organization and will not disclose anything to anyone
                outside our events. I/We hereby swear that I/we are acting on our own behalf as adults and for
                personal interest and recreational purposes only and are not here for any professional
                purposes and are not offended by nudity or sexual expression.
            </p>
        </>
    }

    return (
        <>
            <p>
                I hereby affirm and acknowledge that I am fully aware and understand the
                multitude of inherent hazards and risks to myself and others associated with engaging in sex,
                sadomasochism, BDSM activities and other intimate contact and that such activities could
                result in bodily injury, whether inflicted deliberately or accidentally. I understand and
                acknowledge that not all hazards and dangers can be foreseen. I fully and willingly accept and
                assume all risks that may arise out of or result from my participation in the activities of
                Secrets Hideaway Resort & Spa Dungeon, doing business as Club Swinkster, whose activities are
                held at the establishment operated by Secret Enterprises, LLC (“Secrets”) or any other
                establishment. I understand and acknowledge that all events and activities sponsored by Club
                Swinkster are private events where consensual sex, sadomasochism and other intimate personal
                contact may occur.
            </p>

            <h3>RELEASE OF LIABILITY, WAIVER OF CLAIMS AND INDEMNITY AGREEMENT</h3>

            <p>
                In consideration for being permitted to participate in the activities referenced above, I
                knowingly and voluntarily acknowledge and agree as follows:
            </p>

            <p>
                <b> 1. Assumption of Risk:</b> I am aware that BDSM involves many risks and agree I have no
                pre- existing medical injuries. I am knowingly and voluntarily assuming all risks of injury,
                disability, loss or damage to myself or to any other person or property arising directly or
                indirectly from my participation in sex, BDSM, other intimate activities and any other
                activities occurring at a Club Swinkster event or activity.
            </p>

            <p>
                <b>2. Waiver and Release:</b> I, my estate, heirs, survivors, administrators, representatives,
                agents, employees, consultants, attorneys, executors, successors, insurers, and assigns
                (collectively, “Representatives’) will not make any claim against, sue, or attach the property
                of Secrets Hideaway Resort & Spa, Secret Enterprises LLC, Secrets Development Group LLC,
                Secrets Hideaway resort & Spa Condominium Association INC, SecretsFL.com, Club Secret, any of
                the officers, directors, employees, associates, members and/or volunteers of Club Swinkster,
                or the owners or operators of any of the establishments where Club Swinkster events, meetings
                and activities take place, for personal injury, property damage, injury or damage arising from
                or related to the activities occurring at any Club Swinkster event, whether foreseen or
                unforeseen, resulting from the negligence or other acts, howsoever caused, by any officer,
                member, employee, agent, or contractor of Club Swinkster, or by any other participant in Club
                Swinkster events or activities. I further expressly waive any and all specific notice of the
                existence of the risks that may be present at events of Club Swinkster.
            </p>

            <p>
                <b>3. Indemnity:</b> I agree to <b>INDEMNIFY and HOLD HARMLESS</b> Secrets Hideaway Resort &
                Spa, Secret Enterprises LLC, Secrets Development Group LLC, Secrets Hideaway Resort & Spa
                Condominium Association INC, SecretsFL.com, Club Secret, Club Swinkster, any of the officers,
                directors, employees, associates, members and/or volunteers of Club Swinkster, and the owners
                or operators of any of the establishments where Club Swinkster events, meetings and activities
                take place with respect to any and all injury, disability, loss or damage to person or
                property caused by me, whether due to my negligence or otherwise.
            </p>

            <h3>RULES OF PARTICIPATION</h3>
            <p>
                In consideration for being permitted to participate in the activities referenced above, I
                hereby agree to abide by the following rules:
            </p>

            <p>
                <b>1. Mutual Consent:</b> I understand and agree that all activities require the mutual
                consent of the participants in advance of engaging in BDSM and/or sexual activities. I will
                respect anyone’s decision to decline participation in any proposed activity. I will
                discontinue the activity if any participant withdraws their consent to the activity, and I
                will immediately inform other activity participants if I wish to withdraw my own consent.
            </p>

            <p>
                <b>2. Confidentiality:</b> The identities and personal information of all participants at a
                Club Swinkster event, and the activities they engage in, shall be held by me in strict
                confidence and not disclosed to any third party outside of a Club Swinkster event without
                express permission of that participant.
            </p>

            <h3>EXECUTION, ENFORCEMENT AND RETENTION</h3>
            <p>
                The undersigned acknowledges and warrants that his/hers execution of this Release is free and
                voluntary and understandingly made after careful review of the terms and that he/she has not
                relied on any inducements, promises or representations made by Club Swinkster, Secrets
                Hideaway Resort & Spa, Secret Enterprises LLC, Secrets Development Group LLC, Secrets Hideaway
                Resort & Spa Condominium Association INC, SecretsFL.com, Club Secret, concerning any matters
                stated herein or related thereto. I am not relying on any oral or written representation or
                statements made by any director, officer, member, agent or other representative of Club
                Swinkster or Sanctuary, other than what is set forth in this agreement. This agreement shall
                be binding to the fullest extent permitted by law. If any clause or provision of this Release
                is found to be void, illegal or unenforceable, the remaining provisions and clauses will not
                be affected and shall remain in full force and effect. This Release is governed by and
                interpreted in accordance with the laws of the State of Florida, and is to be construed and
                interpreted as broadly and inclusively as permitted by the laws of the State of Florida.
            </p>
            <p>
                I affirm that I am at least 21 years of age and that I am competent to sign this Release on my
                own behalf. I have read the foregoing document in its entirety, which constitutes the entire
                Agreement between the parties, and I fully understand its contents. I understand that I have
                given up legal rights by signing this agreement. I am aware that this is an Assumption of Risk
                and Release of Liability and I sign this document of my own free will. By signing below, I
                expressly agree to all of the terms contained in this document.
            </p>
        </>
    )
}
